/**
 * A WebSocket that stays connected.
 *
 * - Reconnects after any close it didn't ask for, forever, with exponential backoff
 *   and jitter. API Gateway closes every socket after 2h and drops idle ones after 10m;
 *   networks blip; laptops sleep. None of those are permanent.
 * - Asks for the URL on every attempt, so a refreshed token is picked up.
 * - Heartbeats: sends `ping` and expects `{"type":"pong"}` back. A socket that stops
 *   answering is half-open (common after sleep or a network change, where the browser
 *   never fires `close`) and is replaced.
 * - Reconnects straight away when the browser comes back online or the tab becomes
 *   visible, instead of waiting out the backoff.
 */
export interface TypeSocketOptions {
  /** First retry delay (ms). Doubles each failed attempt. Default 1000. */
  minDelay?: number;
  /** Retry delay ceiling (ms). Default 30000. */
  maxDelay?: number;
  /** How often to ping an open socket (ms). Default 60000 (well inside API Gateway's 10 minute idle timeout). */
  heartbeatInterval?: number;
  /** How long to wait for a pong before the socket is considered dead (ms). Default 10000. */
  pongTimeout?: number;
}

type WebSocketData = string | ArrayBuffer | Blob | ArrayBufferView;

export interface DisconnectInfo {
  code: number;
  /** False when the socket never opened - the handshake itself failed (e.g. an expired token). */
  opened: boolean;
  /** Consecutive attempts that failed to open. */
  failures: number;
}

interface TypeSocketEvents<T> {
  /** `reconnected` is true for every connection after the first. */
  connected: (reconnected: boolean) => void;
  disconnected: (info: DisconnectInfo) => void;
  connecting: () => void;
  message: (message: T) => void;
}

/** Close codes we use ourselves; anything 4000-4999 is application-defined. */
const CLOSE_HEARTBEAT = 4001;
const CLOSE_RESTART = 4002;

const PING = JSON.stringify('ping');

export class TypeSocket<T> {
  private socket: WebSocket | null = null;
  private enabled = false;
  private attempts = 0;
  private failures = 0;
  private everConnected = false;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;

  private options: Required<TypeSocketOptions> = {
    minDelay: 1000,
    maxDelay: 30000,
    heartbeatInterval: 60000,
    pongTimeout: 10000,
  };

  private listeners: { [K in keyof TypeSocketEvents<T>]: Set<TypeSocketEvents<T>[K]> } = {
    connected: new Set(),
    disconnected: new Set(),
    connecting: new Set(),
    message: new Set(),
  };

  /**
   * @param getUrl Called before every connection attempt. Return null when there is
   *   nothing to connect with yet (no token); call `reconnectNow()` once there is.
   */
  constructor(private getUrl: () => string | null, options?: TypeSocketOptions) {
    this.options = { ...this.options, ...options };
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  get isStarted(): boolean {
    return this.enabled;
  }

  /** Starts connecting, and keeps the connection up until `stop()`. */
  start(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.attempts = 0;
    this.listen(true);
    this.open();
  }

  /** Closes the socket and stops reconnecting. */
  stop(): void {
    this.enabled = false;
    this.everConnected = false;
    this.listen(false);
    this.clearRetry();
    this.drop(1000, 'stopped');
  }

  /** Skips any pending backoff and connects now (if not already connected). */
  reconnectNow(): void {
    if (!this.enabled) return;
    this.attempts = 0;
    if (this.socket) return;
    this.clearRetry();
    this.open();
  }

  /** Replaces the current connection, e.g. to pick up a new token. */
  restart(): void {
    if (!this.enabled) return;
    this.attempts = 0;
    this.clearRetry();
    this.drop(CLOSE_RESTART, 'restart');
    this.open();
  }

  send(data: unknown): boolean {
    if (!this.isOpen) return false;
    this.socket.send(JSON.stringify(data));
    return true;
  }

  on<K extends keyof TypeSocketEvents<T>>(event: K, listener: TypeSocketEvents<T>[K]): void {
    this.listeners[event].add(listener);
  }

  off<K extends keyof TypeSocketEvents<T>>(event: K, listener: TypeSocketEvents<T>[K]): void {
    this.listeners[event].delete(listener);
  }

  private emit<K extends keyof TypeSocketEvents<T>>(event: K, ...args: Parameters<TypeSocketEvents<T>[K]>) {
    this.listeners[event].forEach(listener => {
      try {
        (listener as (...a: unknown[]) => void)(...args);
      } catch (e) {
        console.error('[socket] listener failed', e);
      }
    });
  }

  private open() {
    if (!this.enabled || this.socket) return;

    const url = this.getUrl();
    if (!url) return;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // `online` restarts us

    let opened = false;
    const socket = new WebSocket(url);
    this.socket = socket;
    this.emit('connecting');

    socket.onopen = () => {
      if (this.socket !== socket) return;
      opened = true;
      this.attempts = 0;
      this.failures = 0;
      this.startHeartbeat();
      const reconnected = this.everConnected;
      this.everConnected = true;
      this.emit('connected', reconnected);
    };

    socket.onmessage = e => {
      if (this.socket !== socket) return;
      this.receive(e.data);
    };

    // `error` is always followed by `close`; reconnecting is handled there, once.
    socket.onerror = () => {};

    socket.onclose = e => {
      if (this.socket !== socket) return;
      this.closed(e.code, opened);
    };
  }

  private closed(code: number, opened: boolean) {
    this.socket = null;
    this.stopHeartbeat();
    if (!opened) this.failures++;

    this.emit('disconnected', { code, opened, failures: this.failures });

    if (this.enabled) this.scheduleRetry();
  }

  /** Detaches and closes the current socket without triggering a reconnect from its close event. */
  private drop(code: number, reason: string) {
    const socket = this.socket;
    this.socket = null;
    this.stopHeartbeat();
    if (!socket) return;

    socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
    try {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) socket.close(code, reason);
    } catch {}
  }

  private scheduleRetry() {
    this.clearRetry();
    const exp = Math.min(this.options.maxDelay, this.options.minDelay * 2 ** this.attempts);
    // Full jitter on the upper half, so a server blip doesn't reconnect every client at once.
    const delay = exp / 2 + Math.random() * (exp / 2);
    this.attempts++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  private clearRetry() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.ping(), this.options.heartbeatInterval);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.heartbeatTimer = null;
    this.pongTimer = null;
  }

  /** Pings, and replaces the socket if no pong comes back in time. */
  private ping() {
    if (!this.isOpen || this.pongTimer) return;
    this.socket.send(PING);
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      if (!this.socket) return;
      this.drop(CLOSE_HEARTBEAT, 'heartbeat timeout');
      this.closed(CLOSE_HEARTBEAT, true);
    }, this.options.pongTimeout);
  }

  private receive(data: WebSocketData) {
    if (typeof data !== 'string') return;

    let json;
    try {
      json = JSON.parse(data);
    } catch {
      return;
    }

    if (json?.type === 'pong' || json?.type === 'subscribed') {
      if (this.pongTimer) clearTimeout(this.pongTimer);
      this.pongTimer = null;
      return;
    }

    // Any message proves the socket is alive.
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;

    if (json) this.emit('message', json);
  }

  private onOnline = () => this.reconnectNow();

  private onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    // Back from the background (or sleep): check the socket now rather than in up to a minute.
    if (this.socket) this.ping();
    else this.reconnectNow();
  };

  private listen(on: boolean) {
    if (typeof window === 'undefined') return;
    const method = on ? 'addEventListener' : 'removeEventListener';
    window[method]('online', this.onOnline);
    document[method]('visibilitychange', this.onVisible);
  }
}
